// `PremiersPas` settings section (US-187 + US-030).
//
// "Refaire l'onboarding" relaunches the onboarding flow:
//   1. POST /me/onboarding/restart — clears `onboardingCompletedAt` server-side.
//   2. Navigate to `/onboarding` — `OnboardingRedirect` then keeps the user
//      pinned there until they complete the wizard again.
//
// "Contacter le support" — TODO(integration-wave): support-channel decision
// pending (intercom vs. crisp vs. linear-issues). Until it lands we surface a
// public landing-page contact link.

import { useT } from '@barel/i18n';
import { Button, toast } from '@barel/ui';
import { LifeBuoy, RotateCcw } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { useRestartOnboarding } from '@barel/api-client/react';

const SUPPORT_URL = 'https://barel.app/support';

export function PremiersPas() {
  const t = useT('settings');
  const navigate = useNavigate();
  const restart = useRestartOnboarding();

  const handleRelaunch = async (): Promise<void> => {
    try {
      // 1. Wipe completedAt server-side so OnboardingRedirect can fire.
      await restart.mutateAsync();
      // 2. Navigate — OnboardingRedirect keeps the user pinned until done.
      await navigate({ to: '/onboarding' });
    } catch {
      toast.error({ title: t('ftu.relaunchError') });
    }
  };

  return (
    <section
      data-section="start"
      aria-labelledby="settings-start-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1 border-b border-default pb-4">
        <h1 id="settings-start-title" className="text-lg font-semibold text-primary">
          {t('ftu.title')}
        </h1>
        <p className="text-xs text-tertiary">{t('ftu.subtitle')}</p>
      </header>

      <article className="flex flex-col gap-3 rounded-lg border border-default bg-bg-elevated p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-primary">{t('ftu.relaunchTitle')}</h2>
          <p className="text-xs text-secondary">{t('ftu.relaunchHint')}</p>
        </div>
        <div>
          <Button
            type="button"
            size="sm"
            variant="primary"
            iconLeft={<RotateCcw size={14} />}
            loading={restart.isPending}
            onClick={() => {
              void handleRelaunch();
            }}
            data-testid="settings-relaunch"
          >
            {t('ftu.relaunchOnboarding')}
          </Button>
        </div>
      </article>

      <div className="flex items-center justify-between gap-4 border-t border-subtle py-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-primary">{t('ftu.supportTitle')}</span>
          <span className="text-xs text-tertiary">{t('ftu.supportHint')}</span>
        </div>
        {/* TODO(integration-wave): support channel decision pending */}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconLeft={<LifeBuoy size={14} />}
          asChild
          data-testid="settings-support"
        >
          <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            {t('ftu.contactSupport')}
          </a>
        </Button>
      </div>
    </section>
  );
}
